import { useMemo } from 'react';
import useSWR, { type SWRConfiguration } from 'swr';
import { safeValidateAddress } from '@mls-wallet/shared-types';
import denApi from '@/lib/api/denApi';
import type { AddressBookExactEntry } from '@/types/address-book';

type AddressBookEntriesByAddressesParams = {
  addresses: string[];
};

// The endpoint accepts up to 100 addresses, but GET query encoding crosses
// common request-target limits well before that. Keep each URL comfortably
// below those limits while still avoiding one request per address.
const MAX_ADDRESSES_PER_REQUEST = 20;

const fetchBatch = async (
  params: AddressBookEntriesByAddressesParams
): Promise<AddressBookExactEntry[]> => {
  return await denApi.get<
    AddressBookExactEntry[],
    AddressBookEntriesByAddressesParams
  >('organization/address-book/by-addresses', params);
};

const fetcher = async (
  params: AddressBookEntriesByAddressesParams
): Promise<AddressBookExactEntry[]> => {
  const batches = Array.from(
    { length: Math.ceil(params.addresses.length / MAX_ADDRESSES_PER_REQUEST) },
    (_, index) =>
      params.addresses.slice(
        index * MAX_ADDRESSES_PER_REQUEST,
        (index + 1) * MAX_ADDRESSES_PER_REQUEST
      )
  );

  return (
    await Promise.all(batches.map((addresses) => fetchBatch({ addresses })))
  ).flat();
};

/**
 * Fetch exact address-book entries for many distinct known addresses.
 *
 * This is not "many matches for one address" because each address still maps to
 * at most one entry within an organization. The batch form exists so read-only
 * views can enrich known addresses in bounded batch requests instead of
 * firing N single-address lookups or paging through the whole address book.
 */
export function useAddressBookEntriesByAddresses(
  addresses: string[],
  swrOptions?: SWRConfiguration<AddressBookExactEntry[]>
) {
  const normalizedAddresses = useMemo(() => {
    return [
      ...new Set(
        addresses.flatMap((address) => {
          const validation = safeValidateAddress(address);
          return validation.isValid ? [validation.checksumAddress] : [];
        })
      ),
    ].sort();
  }, [addresses]);

  const swrKey =
    normalizedAddresses.length > 0
      ? `organization/address-book/by-addresses/${normalizedAddresses.join('|')}`
      : null;

  const { data, error, isLoading, mutate } = useSWR<AddressBookExactEntry[]>(
    swrKey,
    normalizedAddresses.length > 0
      ? () => fetcher({ addresses: normalizedAddresses })
      : null,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
      ...(swrOptions ?? {}),
    }
  );

  const entries = useMemo(() => data ?? [], [data]);
  const entryByAddress = useMemo(
    () =>
      new Map(
        entries.map((entry) => [entry.address.toLowerCase(), entry] as const)
      ),
    [entries]
  );

  return {
    entries,
    entryByAddress,
    isLoading,
    error,
    refetch: mutate,
  };
}
